import React, { useEffect, useState } from 'react'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { CRMIntegrationSettings } from '../CRMIntegrationSettings'
import { Modal } from '../../../../components/Modal/Modal'
import { Col, Container, Form, Modal as BootstrapModal, Row, Stack, Tab } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../store/user/user'
import {
  engageSettingsDefaultValues,
  EngageSettingsFormValues,
} from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/DynamicsBased/models'
import { Controller, useForm } from 'react-hook-form'
import { getAccountCRMIntegrationConfig } from '../../../../models/Account'
import { IntegrationServiceId } from '../../../../common/integrations'
import { merge } from '../../../Integration/utils'
import { removeEmptyItems } from '../../../../common/util'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Button } from '../../../../components/Button/Button'

export type EngageSettingsModalProps = {
  show: boolean
  onHide: () => void
}

export const EngageSettingsModal: React.FC<EngageSettingsModalProps> = ({ show, onHide }) => {
  const [activeTabKey, setActiveTabKey] = useState<string>('engageMappings')
  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }
  const EngageSettingsModal = () => {
    return (
      <Tabs activeKey={activeTabKey} id='engage-settings' className='d-flex mb-3' onSelect={changeTab} mountOnEnter unmountOnExit>
        <Tab eventKey='engageMappings' title='Mappings'>
          <CRMIntegrationSettings serviceId='engage' onSaved={onHide} onCancelled={onHide} />
        </Tab>
        <Tab eventKey='engageSyncSettings' title='Contact Sync'>
          <EngageContactSyncSettings />
        </Tab>
      </Tabs>
    )
  }
  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<BootstrapModal.Title>Edit SS&C BlackDiamond Engage Integration Settings</BootstrapModal.Title>}
      body={<EngageSettingsModal />}
    />
  )
}

const EngageContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'engage',
    direction: 'inbound',
    object: 'contact_operation',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const form = useForm<EngageSettingsFormValues>({ defaultValues: engageSettingsDefaultValues })
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'engage')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  if (isGetCheckpointFetching) {
    return (
      <>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </>
    )
  }

  const onSubmit = async (data: EngageSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ENGAGE)
      if (integration) {
        let filters: { [key: string]: any } = {}
        if (data.inbound.contact.filters.active !== undefined && data.inbound.contact.filters.active !== null) {
          filters.active = data.inbound.contact.filters.active
        } else {
          filters.active = null
        }
        if (data.inbound.contact.filters.contactType) {
          filters.contactType = data.inbound.contact.filters.contactType
        } else {
          filters.contactType = null
        }
        if ((data.inbound.contact.filters.tags ?? []).length > 0) {
          filters.tags = data.inbound.contact.filters.tags
        } else {
          filters.tags = null
        }

        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters } } })

        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
            >
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
